// src/web/canonicalHeightField.js
//
// A front-facing depth map ("height field") of the MediaPipe canonical face:
// for any (x, y) in canonical space (cm) it answers "how far forward (+Z) is
// the skin surface here?". Built once by rasterising the canonical mesh
// triangles onto a regular grid and keeping the front-most Z per cell.
//
// Used by Glasses.autoSeat(): the frame front (rims, bridge, nose pads) is
// sampled against this surface and the whole frame is pushed forward just
// enough that nothing sits inside the face. Because the live occluder derives
// its depth from the SAME canonical surface (posed by M), a frame that clears
// this height field can never be clipped by the occluder — by construction.

import { CANONICAL_FACE_VERTICES } from './canonicalFace.js';
import { warpedCanonical } from '../core/noseProfile.js';
import { FACEMESH_TESSELATION_INDICES } from './faceMeshTopology.js';

const CELL = 0.2;                 // cm per cell
const X0 = -8.0, X1 = 8.0;        // canonical face spans ±7.75 cm
const Y0 = -9.6, Y1 = 8.4;
const W = Math.round((X1 - X0) / CELL);
const H = Math.round((Y1 - Y0) / CELL);

let _grid = null;
let _noseScale = 1;
let _surface = null;   // per-user surface (canonical frame) from the live occluder

/**
 * Use the PERSON's face as the seating surface: 468 canonical-frame positions
 * (real landmark x,y, canonical depth — what the live occluder actually draws).
 * Pass null to go back to the canonical model. Rebuilds the grid lazily.
 */
export function setHeightFieldSurface(positions) {
  _surface = positions || null; _grid = null;
}

/** Per-user nose depth (see core/noseProfile.js). Rebuilds the grid lazily. */
export function setHeightFieldNoseScale(r) {
  if (Math.abs(r - _noseScale) < 1e-4) return;
  _noseScale = r; _grid = null;
}
export function getHeightFieldNoseScale() { return _noseScale; }

function build() {
  const g = new Float32Array(W * H).fill(NaN);
  const v = _surface ? _surface : (_noseScale === 1 ? CANONICAL_FACE_VERTICES : warpedCanonical(_noseScale));
  const idx = FACEMESH_TESSELATION_INDICES;

  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
    const ax = v[a], ay = v[a + 1], az = v[a + 2];
    const bx = v[b], by = v[b + 1], bz = v[b + 2];
    const cx = v[c], cy = v[c + 1], cz = v[c + 2];

    const det = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    if (Math.abs(det) < 1e-9) continue;   // edge-on triangle

    const minX = Math.max(0, Math.floor((Math.min(ax, bx, cx) - X0) / CELL));
    const maxX = Math.min(W - 1, Math.ceil((Math.max(ax, bx, cx) - X0) / CELL));
    const minY = Math.max(0, Math.floor((Math.min(ay, by, cy) - Y0) / CELL));
    const maxY = Math.min(H - 1, Math.ceil((Math.max(ay, by, cy) - Y0) / CELL));

    for (let gy = minY; gy <= maxY; gy++) {
      const py = Y0 + (gy + 0.5) * CELL;
      for (let gx = minX; gx <= maxX; gx++) {
        const px = X0 + (gx + 0.5) * CELL;
        // Barycentric coordinates of the cell centre.
        const l1 = ((bx - px) * (cy - py) - (cx - px) * (by - py)) / det;
        const l2 = ((cx - px) * (ay - py) - (ax - px) * (cy - py)) / det;
        const l3 = 1 - l1 - l2;
        if (l1 < -1e-4 || l2 < -1e-4 || l3 < -1e-4) continue;
        const z = l1 * az + l2 * bz + l3 * cz;
        const k = gy * W + gx;
        if (!(g[k] >= z)) g[k] = z;        // keep the front-most surface
      }
    }
  }
  return g;
}

/**
 * Front surface depth (+Z, cm) of the canonical face at canonical (x, y).
 * Returns NaN outside the face silhouette.
 */
export function canonicalSurfaceZ(x, y) {
  if (!_grid) _grid = build();
  const gx = Math.floor((x - X0) / CELL);
  const gy = Math.floor((y - Y0) / CELL);
  if (gx < 0 || gy < 0 || gx >= W || gy >= H) return NaN;
  return _grid[gy * W + gx];
}

/**
 * Largest penetration (cm) of a set of points into the canonical face:
 * max over points of (surfaceZ - point.z), or 0 if nothing is inside.
 * `positions` is a flat [x,y,z,...] array in canonical space.
 */
export function maxPenetration(positions, stride = 1) {
  let worst = 0;
  for (let i = 0; i < positions.length; i += 3 * stride) {
    const s = canonicalSurfaceZ(positions[i], positions[i + 1]);
    if (Number.isNaN(s)) continue;
    const pen = s - positions[i + 2];
    if (pen > worst) worst = pen;
  }
  return worst;
}
